/**
 * Fabric inward worklist (garment, `garment.materials`) — the store's
 * cross-order view of incoming materials: every open PO with its received
 * quantity and fabric-inspection state. Unifies the two existing surfaces:
 * "Record inward" opens the generic GRN flow; "Inspect" opens the fabric item's
 * inspection tab.
 *
 * @see packages/domain/src/garment/materials/compute-inward-board.ts
 */
import { Link } from "@tanstack/react-router";
import { PackageOpen, TriangleAlert } from "lucide-react";
import type { materials as materialsContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";

type Board = materialsContract.InwardBoardViewWire;
type Row = Board["rows"][number];

export function InwardPage() {
  const q = trpc.materials.inwardBoard.useQuery();
  const data = q.data;

  return (
    <div className="mx-auto w-full max-w-[1100px] p-6">
      <div className="flex items-center gap-2">
        <PackageOpen size={20} className="text-sky-700" aria-hidden />
        <h1 className="text-xl font-bold tracking-tight text-ink">Fabric inward</h1>
      </div>
      <p className="mt-1 text-sm text-muted">
        Incoming materials across every order — receive against the PO, then inspect the fabric before it hits the floor.
      </p>

      {!data ? (
        <p className="mt-6 text-sm text-muted">Loading…</p>
      ) : data.rows.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed border-line bg-bg px-4 py-10 text-center">
          <p className="text-sm font-medium text-ink-2">No open material POs</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
            Raise vendor POs from a style's Materials tab — they appear here to receive and inspect.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="Awaiting receipt" value={data.totals.awaiting} tone={data.totals.awaiting > 0 ? "text-warn" : undefined} />
            <Stat label="Part-received" value={data.totals.partial} />
            <Stat label="Fabric to inspect" value={data.totals.needsInspection} tone={data.totals.needsInspection > 0 ? "text-bad" : undefined} />
          </div>

          <div className="mt-4 overflow-x-auto rounded-lg border border-line bg-paper">
            <table className="w-full min-w-[820px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                  <th className="px-3 py-2 text-left font-medium">Order · material</th>
                  <th className="px-2 py-2 text-left font-medium">Vendor</th>
                  <th className="px-2 py-2 text-right font-medium">Ordered</th>
                  <th className="px-2 py-2 text-right font-medium">Received</th>
                  <th className="px-2 py-2 text-left font-medium">Receipt</th>
                  <th className="px-2 py-2 text-left font-medium">Inspection</th>
                  <th className="px-3 py-2 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <InwardRow key={r.purchaseOrderId} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function InwardRow({ row }: { row: Row }) {
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-3 py-2">
        <Link to="/merchandising/styles/$styleId/materials" params={{ styleId: row.styleId }} className="font-medium text-ink hover:text-sky-700">
          {row.styleRef}
        </Link>
        <div className="text-xs text-muted">
          {row.label}
          {row.kind !== "fabric" ? <span className="ml-1 opacity-70">· {row.kind}</span> : null}
        </div>
      </td>
      <td className="px-2 py-2 text-ink-2">{row.vendorName}</td>
      <td className="px-2 py-2 text-right font-mono text-ink-2">{fmtQty(row.orderedMilli, row.uom)}</td>
      <td className="px-2 py-2 text-right font-mono text-ink-2">{fmtQty(row.receivedMilli, row.uom)}</td>
      <td className="px-2 py-2">
        <ReceiptChip status={row.receiptStatus} />
      </td>
      <td className="px-2 py-2">
        <InspectionChip inspection={row.inspection} />
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-2">
          {row.receiptStatus !== "received" ? (
            <Link
              to="/procurement/goods-receipts/new"
              search={{ po: row.purchaseOrderId }}
              className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700 hover:brightness-95"
            >
              Record inward →
            </Link>
          ) : null}
          {row.kind === "fabric" && row.itemId ? (
            <Link
              to="/sales/items/$itemId"
              params={{ itemId: row.itemId }}
              search={{ tab: "fabric" }}
              className="rounded-full bg-bg px-2 py-0.5 text-xs font-semibold text-ink-2 hover:brightness-95"
            >
              Inspect →
            </Link>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function ReceiptChip({ status }: { status: Row["receiptStatus"] }) {
  const map = {
    awaiting: { cls: "bg-amber-50 text-warn", label: "Awaiting" },
    partial: { cls: "bg-sky-50 text-sky-700", label: "Part-received" },
    received: { cls: "bg-emerald-50 text-ok", label: "Received" },
  } as const;
  const m = map[status];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}

function InspectionChip({ inspection }: { inspection: Row["inspection"] }) {
  if (inspection === "na") return <span className="text-xs text-muted">—</span>;
  const map = {
    none: { cls: "bg-bg text-muted", label: "Not inspected" },
    pending: { cls: "bg-amber-50 text-warn", label: "Inspect due", icon: true },
    passed: { cls: "bg-emerald-50 text-ok", label: "Passed" },
    flagged: { cls: "bg-red-50 text-bad", label: "Flagged", icon: true },
    hold: { cls: "bg-red-50 text-bad", label: "On hold", icon: true },
  } as const;
  const m = map[inspection];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${m.cls}`}>
      {"icon" in m && m.icon ? <TriangleAlert size={10} aria-hidden /> : null}
      {m.label}
    </span>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string | undefined }) {
  return (
    <div className="rounded-lg border border-line bg-paper p-3">
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className={`mt-0.5 font-mono text-xl font-bold ${tone ?? "text-ink"}`}>{value.toLocaleString("en-IN")}</div>
    </div>
  );
}

/** milli (× 1000 of the uom) → "1,251.9 kg". */
function fmtQty(milli: number, uom: string): string {
  return `${(milli / 1000).toLocaleString("en-IN", { maximumFractionDigits: 1 })} ${uom}`;
}
